import { useState, useEffect } from "react";
import { useLastOrderNumber } from "http-hooks/agorder/useAgorder";
import { useOrderHeader } from "hooks/order";

export default function OrderNumberInput({ isEditMode = false }) {
  const { orderHeader, updateOrderHeader } = useOrderHeader();
  const [useCustomOrderNo, setUseCustomOrderNo] = useState(false);

  // Fixed order type as General
  const orderType = "G";

  // Use the last order number hook only when not in edit mode
  const { data: lastOrderData, isLoading: isLoadingLastOrder } =
    useLastOrderNumber(orderType, !isEditMode);

  // Initialize checkbox state based on existing custom order number (only in create mode)
  useEffect(() => {
    if (!isEditMode && orderHeader.custom_ordno) {
      setUseCustomOrderNo(true);
    }
  }, [orderHeader.custom_ordno, isEditMode]);

  // Handle custom order number toggle (only in create mode)
  const handleCustomOrderToggle = () => {
    if (isEditMode) return; // Prevent changes in edit mode

    const newUseCustom = !useCustomOrderNo;
    setUseCustomOrderNo(newUseCustom);

    if (!newUseCustom) {
      // If turning off custom, clear the custom order number
      updateOrderHeader({ custom_ordno: "" });
    }
  };

  // Handle custom order number input change (only in create mode)
  const handleCustomOrderChange = (e) => {
    if (isEditMode) return; // Prevent changes in edit mode

    const value = e.target.value;
    updateOrderHeader({ custom_ordno: value });
  };

  // If in edit mode, show read-only order number with clear indication
  if (isEditMode) {
    return (
      <div>
        <label
          htmlFor="ordno"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Order No. (Read Only)
        </label>
        <div className="mt-2">
          <input
            id="ordno"
            name="ordno"
            type="text"
            value={orderHeader.ordno || ""}
            readOnly
            className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50 cursor-not-allowed sm:text-sm sm:leading-6"
          />
          <p className="mt-1 text-xs text-gray-500">
            Order numbers cannot be changed after creation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Last Order Number Display with Custom Checkbox (Create Mode Only) */}
      <div className="flex items-center justify-between mb-2">
        <label
          htmlFor="last_ordno"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Last Order No:
        </label>
        <label className="flex items-center text-sm">
          <input
            type="checkbox"
            checked={useCustomOrderNo}
            onChange={handleCustomOrderToggle}
            className="mr-2"
          />
          Use Custom
        </label>
      </div>

      <div className="mt-2">
        <input
          id="last_ordno"
          name="last_ordno"
          type="text"
          value={lastOrderData?.lastOrderNumber || ""}
          readOnly
          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-100 sm:text-sm sm:leading-6"
          placeholder={isLoadingLastOrder ? "Loading..." : "Last Order No."}
        />
      </div>

      {/* Custom Order Number Field - appears when checkbox is checked (Create Mode Only) */}
      {useCustomOrderNo && (
        <div className="mt-3">
          <label
            htmlFor="custom_ordno"
            className="block text-sm font-medium leading-6 text-gray-900 mb-2"
          >
            Custom Order No.
          </label>
          <input
            id="custom_ordno"
            name="custom_ordno"
            type="text"
            autoComplete="custom_ordno"
            value={orderHeader.custom_ordno}
            onChange={handleCustomOrderChange}
            className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Enter custom order number"
          />
          <p className="mt-1 text-xs text-blue-600">
            Custom order number will be used instead of auto-generated number
          </p>
        </div>
      )}
    </div>
  );
}
